<script lang="ts" setup>
import { CONSTRUCTOR_BLOCKS_TYPES } from '~/constants/constructorBlocksTypes';
import type { Brand } from '~/types/Brand';
import type { OfferMedium } from '~/types/Catalog';
import type { ConstructorBlock, StaticPageImage, StaticPageVideo } from '~/types/StaticPageList';
import type { Galleries } from '~/types/MainPage';

const props = defineProps<{ blocks: ConstructorBlock[] }>();

onMounted(() => {
  const scriptBlock = props.blocks.find((b) => b.type === CONSTRUCTOR_BLOCKS_TYPES.SCRIPT);

  if (scriptBlock) {
    useHead({
      script: [
        {
          type: 'text/javascript',
          innerHTML: scriptBlock.data,
          tagPosition: 'head',
        },
      ],
    });
  }
});
</script>

<template>
  <div
    v-if="blocks && blocks.length"
    class="static-page-constructor-blocks"
  >
    <template
      v-for="(block, index) in blocks"
      :key="`block-${index}`"
    >
      <div
        v-if="block.type === CONSTRUCTOR_BLOCKS_TYPES.TEXT"
        class="static-page-constructor-blocks__text"
        v-html="block.text"
      />

      <div
        v-else-if="block.type === CONSTRUCTOR_BLOCKS_TYPES.VIDEO_FRAME"
        class="static-page-constructor-blocks__video"
        v-html="block.frame"
      />

      <ConstructorVideo
        v-else-if="block.type === CONSTRUCTOR_BLOCKS_TYPES.VIDEO"
        :video="block.fields as StaticPageVideo"
      />

      <ConstructorImage
        v-else-if="block.type === CONSTRUCTOR_BLOCKS_TYPES.IMAGE"
        :image="block.fields as StaticPageImage"
      />

      <ConstructorGallery
        v-else-if="block.type === CONSTRUCTOR_BLOCKS_TYPES.GALLERY"
        :images="block.slides as StaticPageImage[]"
      />

      <ConstructorImageGrid
        v-else-if="block.type === CONSTRUCTOR_BLOCKS_TYPES.BORDERED_IMAGE_LIST"
        :images="block.images as StaticPageImage[]"
        bordered
      />

      <ConstructorImageGrid
        v-else-if="block.type === CONSTRUCTOR_BLOCKS_TYPES.BORDERLESS_IMAGE_LIST"
        :images="block.images as StaticPageImage[]"
      />

      <ConstructorTwoImages
        v-else-if="block.type === CONSTRUCTOR_BLOCKS_TYPES.DOUBLE_IMAGE"
        :images="block.images as StaticPageImage[]"
      />

      <ConstructorDataList
        v-else-if="block.type === CONSTRUCTOR_BLOCKS_TYPES.PARAMS_TABLE"
        :params="block.params as { attributes: { title: string; value: string } }[]"
      />

      <ProductCardsGallery
        v-else-if="block.type === CONSTRUCTOR_BLOCKS_TYPES.PRODUCT_GALLERY"
        display-name-mode="without-properties"
        :offers="(block.params as { products?: OfferMedium[] })?.products ?? []"
        :title="(block.params as { title?: string })?.title"
        class="static-page__products-sliders"
        no-padding
      />

      <ProductCardsGallery
        v-else-if="block.type === CONSTRUCTOR_BLOCKS_TYPES.OFFER_GALLERY"
        display-name-mode="without-properties"
        :offers="(block.params as { offers?: OfferMedium[] })?.offers ?? []"
        :title="(block.params as { title?: string })?.title"
        class="static-page__products-sliders"
        no-padding
      />

      <MainBannerGallery
        v-else-if="block.type === CONSTRUCTOR_BLOCKS_TYPES.GALLERY_BANNERS"
        :data="block.data as Galleries"
      />

      <BrandsGallery
        v-else-if="block.type === CONSTRUCTOR_BLOCKS_TYPES.BRANDS"
        :brands="block.brands as Brand[]"
        class="w-full"
      />

      <Tiles
        v-else-if="block.type === CONSTRUCTOR_BLOCKS_TYPES.TILES"
        :data="block.data as Galleries"
      />

      <UnifiedGallery
        v-else-if="block.type === CONSTRUCTOR_BLOCKS_TYPES.GALLERY_UNIFIED"
        :data="block.data as Galleries"
      />
    </template>
  </div>
</template>

<style lang="scss">
.static-page-constructor-blocks {
  display: flex;
  flex-direction: column;
  gap: 32px;
  width: 100%;

  &__text {
    text-align: start;

    iframe {
      width: 100%;
      height: auto;
      border-radius: 12px;
      aspect-ratio: 1/1;
    }

    a {
      color: $label-violet;
      transition:
        color 0.2s ease,
        opacity 0.2s ease;

      &:hover {
        color: $black-80;
        opacity: 0.8;
      }
    }
  }

  &__video {
    width: 100%;

    iframe {
      width: 100%;
      height: auto;
      border-radius: 12px;
      aspect-ratio: 16 / 9;
    }
  }
}
</style>
